import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Clock3, Mail, Printer } from 'lucide-react';
import { DENOMINATIONS } from '@shripos/shared/constants';
import { profileForPaper, shiftReportDocument } from '@shripos/shared/escpos';
import { countedTotal } from '@shripos/shared/reports';
import { Alert, Button, Dialog, Field, Input, Select, Spinner, Textarea, cn, useToast } from '@shripos/ui';
import { ThermalPreview } from '../../components/ThermalPreview.jsx';
import { api } from '../../lib/api.js';
import { rupees, toPaise } from '../../lib/money.js';
import { cachedGet } from '../../lib/offline/cache.js';
import { useSession } from '../../stores/session.js';
import { usePrinting, useReceiptPrinter } from '../../lib/printing/usePrinting.js';

/** The open shift of this outlet: one at a time, shared by everyone billing here (live via 'shift:updated'). */
export function useShift(enabled = true) {
  return useQuery({
    queryKey: ['shift-current'],
    // The last known shift is kept on the device: offline sales go into the same drawer.
    queryFn: () => cachedGet('/shifts/current', `shift:${useSession.getState().me?.outlet?.id}`),
    enabled,
    staleTime: 30_000,
    networkMode: 'always',
  });
}

/** Notes and coins grid → counts; shows the running total. */
export function DenominationGrid({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      {DENOMINATIONS.map((d) => (
        <label key={d} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5 ring-1 ring-slate-200">
          <span className="w-12 text-sm font-semibold tabular-nums">₹{d}</span>
          <span className="text-slate-400">×</span>
          <Input
            inputMode="numeric"
            aria-label={t('shift.notesOrCoins', '₹{{value}} notes or coins', { value: d })}
            className="h-8 px-2"
            value={value[d] ?? ''}
            onChange={(e) => {
              const n = e.target.value.replace(/\D/g, '');
              onChange({ ...value, [d]: n === '' ? undefined : Number(n) });
            }}
          />
        </label>
      ))}
    </div>
  );
}

const cleanCounts = (v) =>
  Object.fromEntries(
    Object.entries(v)
      .filter(([, n]) => Number(n) > 0)
      .map(([d, n]) => [d, Number(n)]),
  );

export function OpenShiftDialog({ onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const outletName = useSession((s) => s.me?.outlet?.name ?? '');
  const [counts, setCounts] = useState({});
  const [byNotes, setByNotes] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const total = byNotes ? countedTotal(cleanCounts(counts)) : (toPaise(amount) ?? 0);
  const open = async () => {
    setBusy(true);
    try {
      const s = await api.post('/shifts/open', {
        openingFloatPaise: total,
        denominations: byNotes ? cleanCounts(counts) : undefined,
      });
      await qc.invalidateQueries({ queryKey: ['shift-current'] });
      toast.success(t('shift.opened', 'Shift #{{no}} opened', { no: s.shiftNo }));
      onClose(s);
    } catch (err) {
      // Someone else at this outlet opened the shift a moment ago: use theirs.
      if (err.code === 'CONFLICT') await qc.invalidateQueries({ queryKey: ['shift-current'] });
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={() => onClose(null)}
      title={t('shift.openAt', 'Open a shift at {{outlet}}', { outlet: outletName })}
      description={t(
        'shift.openHintOutlet',
        "Count the cash in this outlet's drawer. Everyone billing here uses this shift until it is closed.",
      )}
      size="md"
      footer={
        <Button loading={busy} onClick={open} data-testid="open-shift">
          {t('shift.openWith', 'Open shift with {{amount}}', { amount: rupees(total) })}
        </Button>
      }
    >
      <div className="space-y-4">
        {byNotes ? (
          <DenominationGrid value={counts} onChange={setCounts} />
        ) : (
          <Field label={t('shift.openingCash', 'Opening cash (₹)')}>
            <Input
              inputMode="decimal"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('pos.example', 'e.g. {{value}}', { value: 2000 })}
            />
          </Field>
        )}
        <button
          type="button"
          className="text-sm font-medium text-brand-700 hover:underline"
          onClick={() => setByNotes((v) => !v)}
        >
          {byNotes
            ? t('shift.enterTotal', 'Enter a total instead')
            : t('shift.countByNotes', 'Count by notes and coins')}
        </button>
      </div>
    </Dialog>
  );
}

export function CashEntryDialog({ categories, onClose }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [type, setType] = useState('out');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState(categories[0] ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post('/shifts/cash', {
        type,
        amountPaise: toPaise(amount) ?? 0,
        category: type === 'out' ? category : null,
        note,
      });
      await qc.invalidateQueries({ queryKey: ['shift-current'] });
      toast.success(
        type === 'out'
          ? t('shift.cashOutRecorded', 'Cash out {{amount}} recorded', { amount: rupees(toPaise(amount)) })
          : t('shift.cashInRecorded', 'Cash in {{amount}} recorded', { amount: rupees(toPaise(amount)) }),
      );
      onClose();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('shift.cashInOut', 'Cash in / out')}
      size="sm"
      footer={
        <Button loading={busy} disabled={!(toPaise(amount) > 0)} onClick={save}>
          {t('common.save', 'Save')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div
          className="grid grid-cols-2 rounded-lg ring-1 ring-slate-300"
          role="radiogroup"
          aria-label={t('shift.type', 'Type')}
        >
          {[
            ['out', t('shift.cashOut', 'Cash out (expense)')],
            ['in', t('shift.cashIn', 'Cash in')],
          ].map(([k, l]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={type === k}
              onClick={() => setType(k)}
              className={cn('py-2 text-sm font-semibold', type === k ? 'bg-slate-900 text-white' : 'text-slate-600')}
            >
              {l}
            </button>
          ))}
        </div>
        <Field label={t('pos.amount', 'Amount (₹)')}>
          <Input inputMode="decimal" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        {type === 'out' && (
          <Field label={t('shift.category', 'Category')}>
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t('shift.note', 'Note')}>
          <Input
            value={note}
            maxLength={160}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              type === 'out'
                ? t('shift.noteOutExample', 'e.g. coriander & curry leaves')
                : t('shift.noteInExample', 'e.g. change from bank')
            }
          />
        </Field>
      </div>
    </Dialog>
  );
}

/** Thermal-style view of an X / shift report with print and email. */
export function ShiftReportView({ report, shiftId, canEmail }) {
  const { t } = useTranslation();
  const printer = useReceiptPrinter();
  const printing = usePrinting();
  const toast = useToast();
  const profile = useMemo(() => printer?.profile ?? profileForPaper('80mm'), [printer]);
  const doc = useMemo(() => shiftReportDocument(report, { profile }), [report, profile]);
  return (
    <div className="space-y-3">
      <div className="max-h-[55vh] overflow-auto rounded-lg bg-slate-200 p-3">
        <ThermalPreview doc={doc} profile={profile} className="shadow" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          icon={Printer}
          onClick={() =>
            printing.report((p) => shiftReportDocument(report, { profile: p }), {
              label: report.kind === 'X' ? 'X report' : 'Shift report',
            })
          }
        >
          {t('pos.print', 'Print')}
        </Button>
        {canEmail && (
          <Button
            variant="secondary"
            icon={Mail}
            onClick={() =>
              api
                .post(`/shifts/${shiftId}/email`, {})
                .then((r) => toast.success(t('shift.sentTo', 'Sent to {{to}}', { to: r.to })))
                .catch((err) => toast.error(err))
            }
          >
            {t('shift.emailOwner', 'Email owner')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function CloseShiftDialog({ shift, canManage, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [counts, setCounts] = useState({});
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const counted = countedTotal(cleanCounts(counts), toPaise(other) ?? 0);
  const showExpected = !shift.blind && shift.expectedPaise != null;
  const close = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/shifts/${shift.id}/close`, {
        denominations: cleanCounts(counts),
        otherPaise: toPaise(other) ?? 0,
        note,
      });
      await qc.invalidateQueries({ queryKey: ['shift-current'] });
      qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.invalidateQueries({ queryKey: ['day-status'] });
      setResult(r);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  if (result) {
    const v = result.report.cash.variancePaise;
    return (
      <Dialog
        open
        onClose={() => onClose(result)}
        title={t('shift.closed', 'Shift #{{no}} closed', { no: shift.shiftNo })}
        size="md"
        footer={<Button onClick={() => onClose(result)}>{t('pos.done', 'Done')}</Button>}
      >
        <div className="space-y-3">
          <Alert tone={v === 0 ? 'success' : 'warning'}>
            {v === 0
              ? t('shift.matches', 'The drawer matches exactly.')
              : v > 0
                ? t('shift.over', 'The drawer has {{amount}} more than expected.', { amount: rupees(v) })
                : t('shift.short', 'The drawer is short by {{amount}}.', { amount: rupees(-v) })}
          </Alert>
          <ShiftReportView report={result.report} shiftId={shift.id} canEmail={canManage} />
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      open
      onClose={() => onClose(null)}
      title={t('shift.closeTitle', 'Close shift #{{no}}', { no: shift.shiftNo })}
      description={
        shift.blind
          ? t(
              'shift.countBlind',
              'Count every note and coin in the drawer. The expected amount is shown after you close.',
            )
          : t('shift.count', 'Count every note and coin in the drawer.')
      }
      size="lg"
      footer={
        <Button loading={busy} onClick={close} data-testid="close-shift">
          {t('shift.closeCounted', 'Close shift · counted {{amount}}', { amount: rupees(counted) })}
        </Button>
      }
    >
      <div className="space-y-4">
        <DenominationGrid value={counts} onChange={setCounts} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('shift.coinsOther', 'Coins / other (₹)')}>
            <Input inputMode="decimal" value={other} onChange={(e) => setOther(e.target.value)} />
          </Field>
          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            <p>
              {t('shift.counted', 'Counted')}: <strong>{rupees(counted)}</strong>
            </p>
            {showExpected && (
              <>
                <p>
                  {t('shift.expected', 'Expected')}: {rupees(shift.expectedPaise)}
                </p>
                <p
                  className={cn(
                    'font-semibold',
                    counted - shift.expectedPaise < 0 ? 'text-red-700' : 'text-emerald-700',
                  )}
                >
                  {t('shift.difference', 'Difference')}: {rupees(counted - shift.expectedPaise)}
                </p>
              </>
            )}
          </div>
        </div>
        <Field label={t('shift.notes', 'Notes')}>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** Billing header: current shift; tap for cash in/out, X report and closing. */
export function ShiftChip({ shiftQuery, onOpen, onDialogChange }) {
  const { t } = useTranslation();
  // 'menu' | 'cash' | 'x' | { type: 'close', shift } — the close dialog keeps its own copy of the
  // shift so it can show the result after the shift (and this chip) has changed.
  const [dialog, setDialog] = useState(null);
  useEffect(() => onDialogChange?.(Boolean(dialog)), [dialog, onDialogChange]);
  const data = shiftQuery.data;
  const shift = data?.shift;
  const canManage = data?.canManage;
  const outletName = useSession((s) => s.me?.outlet?.name ?? '');
  const report = useQuery({
    queryKey: ['shift-report', shift?.id],
    queryFn: () => api.get(`/shifts/${shift.id}/report`),
    enabled: dialog === 'x' && Boolean(shift),
  });
  if (!data) return null;
  return (
    <>
      {shift ? (
        <Button
          variant="secondary"
          icon={Banknote}
          onClick={() => setDialog('menu')}
          data-testid="shift-chip"
          title={`${t('shift.shiftNo', 'Shift #{{no}}', { no: shift.shiftNo })} · ${shift.currentCashier}`}
        >
          <span className="hidden lg:inline">{t('shift.shiftNo', 'Shift #{{no}}', { no: shift.shiftNo })}</span>
        </Button>
      ) : (
        <Button variant="secondary" icon={Clock3} onClick={onOpen} data-testid="shift-chip">
          <span className="hidden lg:inline">{t('shift.open', 'Open shift')}</span>
        </Button>
      )}
      {dialog === 'menu' && shift && (
        <Dialog
          open
          onClose={() => setDialog(null)}
          title={`${t('shift.shiftNo', 'Shift #{{no}}', { no: shift.shiftNo })} · ${outletName}`}
          description={t('shift.openedBy', 'Opened by {{name}} · {{count}} bills', {
            name: shift.openedBy,
            count: shift.billCount,
          })}
          size="sm"
        >
          <div className="grid gap-2">
            <Button variant="secondary" onClick={() => setDialog('cash')}>
              {t('shift.cashInOut', 'Cash in / out')}
            </Button>
            {!shift.blind && (
              <Button variant="secondary" onClick={() => setDialog('x')}>
                {t('shift.xReportMid', 'X report (mid-shift)')}
              </Button>
            )}
            <Button variant="danger-outline" onClick={() => setDialog({ type: 'close', shift })}>
              {t('shift.close', 'Close shift')}
            </Button>
          </div>
        </Dialog>
      )}
      {dialog === 'cash' && (
        <CashEntryDialog categories={data.settings.cashOutCategories} onClose={() => setDialog(null)} />
      )}
      {dialog === 'x' && shift && (
        <Dialog open onClose={() => setDialog(null)} title={t('shift.xReport', 'X report')} size="md">
          {report.isLoading ? (
            <Spinner />
          ) : report.error ? (
            <Alert tone="danger">{report.error.message}</Alert>
          ) : (
            <ShiftReportView report={report.data.report} shiftId={shift.id} canEmail={canManage} />
          )}
        </Dialog>
      )}
      {dialog?.type === 'close' && (
        <CloseShiftDialog shift={dialog.shift} canManage={canManage} onClose={() => setDialog(null)} />
      )}
    </>
  );
}
